import { useEffect, useState } from "react";
import { getCatalogPayers, type IPayerCapability } from "../services/api";
import { catalogPayerDisplayName } from "../utils/catalog";

/**
 * Module-level cache of catalog payers per (destination, payout method). The
 * claim pins a single payer, so the list is fetched once and reused by any
 * screen that needs to name it.
 */
const cache = new Map<string, IPayerCapability[]>();
const inflight = new Map<string, Promise<IPayerCapability[]>>();

export const loadCatalogPayers = (
  destinationCountryCode: string,
  payoutMethod: string | undefined
): Promise<IPayerCapability[]> => {
  const key = `${destinationCountryCode}|${payoutMethod ?? ""}`;
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = getCatalogPayers({
    destinationCountryCode,
    payoutMethod,
    includeUnavailable: true,
  })
    .then((items) => {
      cache.set(key, items);
      inflight.delete(key);
      return items;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
};

export interface PayerNameState {
  /** Catalog `displayName`, or empty when unresolved. Never a raw payer code. */
  name: string;
  /** True while the catalog lookup for this code is in flight. */
  pending: boolean;
}

/**
 * Human-readable name for a canonical payer code, resolved from the catalog
 * (`displayName`). `name` stays empty while the catalog loads or the code
 * cannot resolve — never a title-cased or raw payer code.
 */
export const usePayerName = (
  payerCode: string | undefined,
  destinationCountry: string,
  payoutMethod?: string
): PayerNameState => {
  const requestKey = `${payerCode ?? ""}|${destinationCountry}|${payoutMethod ?? ""}`;
  const [seenKey, setSeenKey] = useState(requestKey);
  const [displayName, setDisplayName] = useState<string | null | undefined>(
    undefined
  );

  if (seenKey !== requestKey) {
    setSeenKey(requestKey);
    setDisplayName(undefined);
  }

  useEffect(() => {
    if (!payerCode || !destinationCountry) {
      setDisplayName(null);
      return;
    }

    let active = true;

    loadCatalogPayers(destinationCountry, payoutMethod)
      .then((items) => {
        if (!active) return;
        const foundName = catalogPayerDisplayName(payerCode, items);
        setDisplayName(foundName);
      })
      .catch(() => {
        if (active) setDisplayName(null);
      });

    return () => {
      active = false;
    };
  }, [payerCode, destinationCountry, payoutMethod]);

  if (!payerCode || !destinationCountry) return { name: "", pending: false };
  if (seenKey !== requestKey || displayName === undefined) {
    return { name: "", pending: true };
  }
  return { name: displayName ?? "", pending: false };
};
